import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { Table, TableBody, Typography, TableContainer, TableHead, TableRow, Paper, IconButton, Stack, Pagination, Box, Chip, FormControl, Select, MenuItem, Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox, Autocomplete } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import WriteGuard from '../../../../components/auth/WriteGuard';
import PrintIcon from '@mui/icons-material/Print';
import CancelIcon from '@mui/icons-material/Cancel';
import { toast } from 'react-toastify';
import SearchInput from '../../../../utils/searchInput';
import { dayStartISO, dayEndISO, localTodayYMD } from '../../../../utils/dateRange';
import { fetchAndPrintLabels } from '../../../../utils/printLabels';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getLabOrderRequest, cancelLabOrderRequest, resetCancelLabOrder, bulkReleaseLabOrderRequest, resetBulkReleaseLabOrder } from '../../../../redux/modules/laboratory/labOrder/slice';
import { getLabTestRequest } from '../../../../redux/modules/laboratory/labTest/slice';
import { getLabTestPanelRequest } from '../../../../redux/modules/laboratory/labTestPanel/slice';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white },
    [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:last-child td, &:last-child th': { border: 0 },
}));

const statusColors = {
    ordered: 'info', collected: 'warning', received: 'secondary', processing: 'primary',
    resulted: 'success', verified: 'success', released: 'default', cancelled: 'error'
};

const priorityColors = { routine: 'default', urgent: 'warning', stat: 'error' };

let limit = 10;

// Open a print-friendly worklist of the given orders (patient, tests, accession) in a new
// window and trigger the browser print dialog. Returns false if the pop-up was
// blocked. Patient-supplied values are HTML-escaped before injection.
//
// The sheet is sectioned by lab department (Haematology, Biochemistry, ...) so
// each bench can work its own list: an order with tests in two departments is
// listed under both, showing only that department's tests each time. Section
// names come from the export API, which resolves them the same way the printed
// report does. Where no test carries a department — a catalog with no departments
// set up — everything lands in one section and prints as a single plain table.
function printOrdersWorklist(orders, dateStr) {
    const esc = (v) => String(v == null ? '' : v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const UNSECTIONED = 'Other';
    const isStat = (o) => String(o?.priority || '').toLowerCase() === 'stat';

    // Bucket every order under each department it has tests in. `sort` is the
    // department's catalog sortOrder, so sections print bench order, not
    // alphabetically; a nameless department sorts last with the strays.
    const sections = new Map();
    for (const o of orders) {
        const byDept = new Map();
        for (const t of (o.tests || [])) {
            const name = t?.name || '';
            if (!name) continue;
            const dept = t.department || UNSECTIONED;
            const sort = t.departmentSort == null ? 999 : t.departmentSort;
            if (!byDept.has(dept)) byDept.set(dept, { sort, names: [] });
            byDept.get(dept).names.push(name);
        }
        // An order with no live tests still belongs on the sheet.
        if (byDept.size === 0) byDept.set(UNSECTIONED, { sort: 999, names: [] });
        for (const [dept, bucket] of byDept) {
            if (!sections.has(dept)) sections.set(dept, { name: dept, sort: bucket.sort, rows: [] });
            const s = sections.get(dept);
            if (bucket.sort < s.sort) s.sort = bucket.sort;
            s.rows.push({ order: o, tests: bucket.names });
        }
    }
    // STAT orders work first, so they lead their section — the bench reads top-down
    // and the urgent ones are always the first rows under the heading. Array sort is
    // stable, so everything else keeps the API's chronological order.
    for (const s of sections.values()) {
        s.rows.sort((a, b) => (isStat(b.order) ? 1 : 0) - (isStat(a.order) ? 1 : 0));
        s.statCount = s.rows.filter((r) => isStat(r.order)).length;
    }
    const ordered = [...sections.values()].sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));

    const row = (entry, i) => {
        const o = entry.order;
        const stat = isStat(o);
        return `
        <tr${stat ? ' class="stat"' : ''}>
            <td>${i + 1}</td>
            <td>${stat ? '<span class="tag">STAT</span>' : ''}${esc(o.patientName) || '-'}</td>
            <td>${esc(o.mrn) || '-'}</td>
            <td>${esc(o.accessionNumber) || '-'}</td>
            <td>${esc(entry.tests.join(', ')) || '-'}</td>
            <td>${esc(o.status) || '-'}</td>
            <td>${o.orderedAt ? esc(new Date(o.orderedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })) : '-'}</td>
        </tr>`;
    };
    const table = (rows) => `<table>
                <thead><tr><th>#</th><th>Patient Name</th><th>MRN</th><th>Accession #</th><th>Tests</th><th>Status</th><th>Time</th></tr></thead>
                <tbody>${rows.map(row).join('') || '<tr><td colspan="7" style="text-align:center">No orders for this day</td></tr>'}</tbody>
            </table>`;

    // One department (or none, on an empty day) needs no headings.
    const body = ordered.length > 1
        ? ordered.map((s) => `<section>
                <h2>${esc(s.name)} <span class="count">${s.statCount ? `${s.statCount} STAT &middot; ` : ''}${s.rows.length} order(s)</span></h2>
                ${table(s.rows)}
            </section>`).join('')
        : table(ordered[0]?.rows || []);
    const statOrders = orders.filter(isStat).length;
    const statNote = statOrders ? ` &middot; ${statOrders} STAT (listed first)` : '';
    const meta = ordered.length > 1
        ? `${orders.length} order(s)${statNote} &middot; ${ordered.length} departments &middot; an order with tests in more than one department is listed under each`
        : `${orders.length} order(s)${statNote}`;

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Lab Orders ${esc(dateStr)}</title>
        <style>
            body { font-family: Arial, Helvetica, sans-serif; margin: 24px; color: #111; }
            h1 { font-size: 18px; margin: 0 0 4px; }
            h2 { font-size: 13px; margin: 18px 0 6px; text-transform: uppercase; letter-spacing: 0.04em;
                 border-bottom: 2px solid #333; padding-bottom: 3px;
                 /* keep a department heading with its first rows */
                 break-after: avoid; page-break-after: avoid; }
            h2 .count { font-weight: normal; text-transform: none; letter-spacing: 0; color: #555; float: right; }
            .meta { font-size: 12px; color: #555; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #999; padding: 6px 8px; text-align: left; }
            th { background: #f0f0f0; }
            /* Repeat the column headers when a section runs onto the next page. */
            thead { display: table-header-group; }
            tr { break-inside: avoid; page-break-inside: avoid; }
            /* STAT marking has to survive a mono laser print with Chrome's
               "Background graphics" off (the default), which drops every fill and
               shading. So it is carried entirely by rules and weight — a box border
               round the tag, a heavy rule down the left of the row, bold text — and
               never by a filled swatch or a colour. */
            tr.stat td { font-weight: bold; }
            tr.stat td:first-child { border-left: 3px solid #000; }
            .tag { display: inline-block; border: 1.5px solid #000; border-radius: 2px;
                   padding: 0 4px; margin-right: 5px; font-size: 10px; font-weight: bold;
                   letter-spacing: 0.06em; white-space: nowrap; }
        </style></head>
        <body>
            <h1>Lab Orders &mdash; ${esc(dateStr)}</h1>
            <div class="meta">${meta}</div>
            ${body}
            <script>window.onload = function () { window.print(); }</script>
        </body></html>`;
    // The sheet is written into a blank pop-up, so the window's URL is literally
    // about:blank. With the browser's "Headers and footers" print option on (Chrome's
    // default) that URL is stamped in the bottom-left of every page, alongside the
    // date top-left and page numbers bottom-right. It's a per-workstation browser
    // setting, not something the page can turn off, which is why it shows up on some
    // machines and not others: untick it once in the print dialog's More settings.
    const w = window.open('', '_blank');
    if (!w) return false;
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Chrome prints the document URL in the bottom-left of every page, opposite its
    // page number ("Headers and footers", on by default and sticky per browser
    // profile — which is why only some workstations show it). A pop-up written to
    // like this has no URL of its own, so that corner came out reading "about:blank".
    // Both live in the one footer, so the page number cannot be kept while the URL is
    // dropped. Give the pop-up a real URL instead: it inherits this window's origin,
    // so the rewrite is same-origin, and the orders list is where a reload of the
    // pop-up should land. Cosmetic only — nothing here navigates — and a browser that
    // refuses the rewrite simply keeps the old about:blank.
    try {
        const base = window.location.pathname.split('/laboratory')[0];
        w.history.replaceState({}, '', `${base}/laboratory/orders`);
    } catch { /* leave the URL as it was */ }
    return true;
}

export default function LabOrders() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const basePath = location.pathname.split('/laboratory')[0];
    let { getRes, loading, cancelRes, bulkReleaseRes } = useSelector(state => state.labOrder);
    let { currentBranch } = useSelector(state => state.branch);
    const allTests = useSelector(state => state.labTest.getRes?.data);
    const allPanels = useSelector(state => state.labTestPanel.getRes?.data);
    // Filters + page seed from the URL so returning to this list (Back button or
    // browser back) restores the same view. They're written back to the URL below.
    const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || "");
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || "");
    const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || "");
    const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || '');
    const [dateTo, setDateTo] = useState(searchParams.get('to') || '');
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [testPanelFilter, setTestPanelFilter] = useState(null);
    const hydrated = useRef(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelOrderId, setCancelOrderId] = useState(null);

    useEffect(() => {
        dispatch(setMainHeading("Lab Orders"));
        dispatch(getLabTestRequest({ page: 1, limit: 500, searchQuery: '' }));
        dispatch(getLabTestPanelRequest({ page: 1, limit: 100, searchQuery: '' }));
    }, [dispatch]);

    // Test/Panel filter — combined searchable options (orderable panels + tests)
    // and the active id resolved by kind (sent to the list + print/export).
    const testPanelOptions = [
        ...(allPanels || []).filter(p => p.isOrderable !== false).map(p => ({ kind: 'panel', _id: p._id, code: p.panelCode, name: p.name })),
        ...(allTests || []).filter(t => t.isOrderable !== false).map(t => ({ kind: 'test', _id: t._id, code: t.testCode, name: t.name })),
    ];
    const activeTestId = testPanelFilter?.kind === 'test' ? testPanelFilter._id : undefined;
    const activePanelId = testPanelFilter?.kind === 'panel' ? testPanelFilter._id : undefined;

    // Restore the Test/Panel filter object from the URL once the option lists load
    // (the URL only carries the id). `hydrated` gates the URL-writer below so it
    // can't wipe the test/panel param before we've had a chance to reconstruct it.
    useEffect(() => {
        const t = searchParams.get('test');
        const pn = searchParams.get('panel');
        if (!t && !pn) { hydrated.current = true; return; }
        if (t && allTests?.length) {
            const o = allTests.find(x => x._id === t);
            if (o) setTestPanelFilter({ kind: 'test', _id: o._id, code: o.testCode, name: o.name });
            hydrated.current = true;
        } else if (pn && allPanels?.length) {
            const o = allPanels.find(x => x._id === pn);
            if (o) setTestPanelFilter({ kind: 'panel', _id: o._id, code: o.panelCode, name: o.name });
            hydrated.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allTests, allPanels]);

    // Persist filters + page to the URL (replace, so keystrokes don't stack history).
    useEffect(() => {
        if (!hydrated.current) return;
        const p = {};
        if (searchQuery) p.q = searchQuery;
        if (statusFilter) p.status = statusFilter;
        if (priorityFilter) p.priority = priorityFilter;
        if (dateFrom) p.from = dateFrom;
        if (dateTo) p.to = dateTo;
        if (currentPage > 1) p.page = String(currentPage);
        if (activeTestId) p.test = activeTestId;
        if (activePanelId) p.panel = activePanelId;
        setSearchParams(p, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, statusFilter, priorityFilter, dateFrom, dateTo, currentPage, testPanelFilter]);

    useEffect(() => {
        if (!currentBranch) return;
        dispatch(getLabOrderRequest({ page: currentPage, limit, searchQuery, status: statusFilter, priority: priorityFilter, dateFrom: dayStartISO(dateFrom), dateTo: dayEndISO(dateTo), testId: activeTestId, panelId: activePanelId }));
    }, [dispatch, currentBranch, currentPage, searchQuery, statusFilter, priorityFilter, dateFrom, dateTo, testPanelFilter]);

    useEffect(() => {
        if (cancelRes) {
            if (cancelRes.success) {
                toast("Order cancelled");
                dispatch(getLabOrderRequest({ page: currentPage, limit, searchQuery, status: statusFilter, priority: priorityFilter, dateFrom: dayStartISO(dateFrom), dateTo: dayEndISO(dateTo), testId: activeTestId, panelId: activePanelId }));
            } else {
                toast(cancelRes.message || "Cancel failed");
            }
            dispatch(resetCancelLabOrder());
        }
    }, [cancelRes, dispatch, currentPage, searchQuery, statusFilter, priorityFilter, dateFrom, dateTo, testPanelFilter]);

    useEffect(() => {
        if (bulkReleaseRes) {
            if (bulkReleaseRes.success) {
                toast(bulkReleaseRes.message);
                setSelectedOrders([]);
                dispatch(getLabOrderRequest({ page: currentPage, limit, searchQuery, status: statusFilter, priority: priorityFilter, dateFrom: dayStartISO(dateFrom), dateTo: dayEndISO(dateTo), testId: activeTestId, panelId: activePanelId }));
            } else {
                toast(bulkReleaseRes.message || "Bulk release failed");
            }
            dispatch(resetBulkReleaseLabOrder());
        }
    }, [bulkReleaseRes, dispatch, currentPage, searchQuery, statusFilter, priorityFilter, dateFrom, dateTo, testPanelFilter]);

    // Specimen labels go to the lab's label printer. The browser's print dialog
    // still picks the device on every job, so report/invoice printing — which
    // goes to the ordinary office printer — is unaffected.
    const handlePrintLabels = async (orderId) => {
        const result = await fetchAndPrintLabels(
            `${process.env.REACT_APP_SERVER_API}api/laboratory/orders/${orderId}/labels`,
            { authToken: localStorage.getItem("item"), branchId: currentBranch?.id }
        );
        if (!result.ok) toast(result.message);
    };

    const handleExportToday = async () => {
        try {
            const authToken = localStorage.getItem("item");
            const todayStr = localTodayYMD();
            // Send the user's local day as exact UTC instants so "today" matches the
            // viewer's timezone regardless of the API server's timezone.
            const res = await fetch(`${process.env.REACT_APP_SERVER_API}api/laboratory/orders/export?dateFrom=${encodeURIComponent(dayStartISO(todayStr))}&dateTo=${encodeURIComponent(dayEndISO(todayStr))}`, {
                headers: { Authorization: `Bearer ${authToken}`, 'X-Current-Branch': currentBranch?.id || '' }
            });
            const json = await res.json();
            if (!res.ok || !json.success) { toast(json.message || "Failed to export orders"); return; }
            if (!printOrdersWorklist(json.data || [], todayStr)) {
                toast("Allow pop-ups for this site to print the worklist");
            }
        } catch { toast("Failed to export orders"); }
    };

    // Print the checked-off orders if any are selected, otherwise the current
    // filter (From/To date + status/priority + test/panel). Dates are sent as the
    // viewer's local start/end of day (exact instants), matching Print Today's.
    const handlePrintFiltered = async () => {
        try {
            const authToken = localStorage.getItem("item");
            let query, label;
            if (selectedOrders.length > 0) {
                query = `orderIds=${encodeURIComponent(selectedOrders.join(','))}`;
                label = `${selectedOrders.length} selected`;
            } else if (dateFrom || dateTo || testPanelFilter) {
                const parts = [];
                if (dateFrom) parts.push(`dateFrom=${encodeURIComponent(dayStartISO(dateFrom))}`);
                if (dateTo) parts.push(`dateTo=${encodeURIComponent(dayEndISO(dateTo))}`);
                if (statusFilter) parts.push(`status=${encodeURIComponent(statusFilter)}`);
                if (priorityFilter) parts.push(`priority=${encodeURIComponent(priorityFilter)}`);
                if (activeTestId) parts.push(`testId=${encodeURIComponent(activeTestId)}`);
                if (activePanelId) parts.push(`panelId=${encodeURIComponent(activePanelId)}`);
                query = parts.join('&');
                const dateLabel = dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : (dateFrom || dateTo);
                label = [dateLabel, testPanelFilter?.code].filter(Boolean).join(' · ') || 'Filtered orders';
            } else {
                toast("Check some orders, or pick a date / test / panel, to print");
                return;
            }
            const res = await fetch(`${process.env.REACT_APP_SERVER_API}api/laboratory/orders/export?${query}`, {
                headers: { Authorization: `Bearer ${authToken}`, 'X-Current-Branch': currentBranch?.id || '' }
            });
            const json = await res.json();
            if (!res.ok || !json.success) { toast(json.message || "Failed to export orders"); return; }
            if (!(json.data || []).length) { toast("No orders match the selection"); return; }
            if (!printOrdersWorklist(json.data, label)) {
                toast("Allow pop-ups for this site to print the worklist");
            }
        } catch { toast("Failed to export orders"); }
    };

    const handleCancel = (orderId) => {
        setCancelOrderId(orderId);
        setCancelOpen(true);
    };
    const confirmCancel = () => {
        dispatch(cancelLabOrderRequest({ id: cancelOrderId, reason: 'Cancelled from order list' }));
        setCancelOpen(false);
        setCancelOrderId(null);
    };

    const handleBulkRelease = () => {
        const verifiedIds = selectedOrders.filter(id => {
            const order = getRes?.data?.find(o => o._id === id);
            return order?.status === 'verified';
        });
        if (verifiedIds.length === 0) { toast("Select verified orders to release"); return; }
        dispatch(bulkReleaseLabOrderRequest({ orderIds: verifiedIds }));
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedOrders(getRes?.data?.map(o => o._id) || []);
        } else {
            setSelectedOrders([]);
        }
    };

    const handleSelectOrder = (id) => {
        setSelectedOrders(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const pageCount = getRes?.dataCount > limit ? Math.ceil(getRes.dataCount / limit) : 0;

    return (
        <>
            <Box textAlign="right" mb={3} mt={4}>
                <Button variant="outlined" startIcon={<PrintIcon />} sx={{ mr: 1 }} onClick={handleExportToday}>
                    Print Today's Orders
                </Button>
                <Button variant="outlined" startIcon={<PrintIcon />} sx={{ mr: 1 }} onClick={handlePrintFiltered}>
                    {selectedOrders.length > 0 ? `Print Selected (${selectedOrders.length})` : 'Print Filtered'}
                </Button>
                <WriteGuard module="labOrders">
                    <Button className="commonBtn" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${basePath}/laboratory/orders/new`)}>
                        New Order
                    </Button>
                </WriteGuard>
            </Box>
            <Stack mb={3} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" flexWrap="wrap">
                <SearchInput defaultValue={searchQuery} setSearchQuery={(v) => { setSearchQuery(v); setCurrentPage(1); }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                    <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                        value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                        sx={{ minWidth: 140 }} />
                    <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                        value={dateTo} onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                        sx={{ minWidth: 140 }} />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} displayEmpty>
                            <MenuItem value="">All Status</MenuItem>
                            {['ordered', 'collected', 'received', 'processing', 'resulted', 'verified', 'released', 'cancelled'].map(s => (
                                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(1); }} displayEmpty>
                            <MenuItem value="">All Priority</MenuItem>
                            {['routine', 'urgent', 'stat'].map(p => (
                                <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Autocomplete
                        size="small" sx={{ minWidth: 220 }}
                        options={testPanelOptions}
                        value={testPanelFilter}
                        onChange={(e, v) => { setTestPanelFilter(v); setCurrentPage(1); }}
                        getOptionLabel={(o) => `${o.code} - ${o.name}${o.kind === 'panel' ? ' (Panel)' : ''}`}
                        isOptionEqualToValue={(o, v) => o.kind === v.kind && o._id === v._id}
                        renderInput={(params) => <TextField {...params} label="Test / Panel" />}
                        renderOption={(props, o) => (
                            <li {...props} key={`${o.kind}-${o._id}`}>
                                {o.code} - {o.name}
                                {o.kind === 'panel' && <Chip label="Panel" size="small" variant="outlined" sx={{ ml: 1, height: 18 }} />}
                            </li>
                        )}
                    />
                </Stack>
            </Stack>
            {selectedOrders.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" fontWeight={600}>{selectedOrders.length} selected</Typography>
                    <WriteGuard capability="labResultAuth:write">
                        <Button size="small" variant="contained" color="success" startIcon={<SendIcon />} onClick={handleBulkRelease} disabled={loading}>
                            Release Selected
                        </Button>
                    </WriteGuard>
                    <Button size="small" variant="outlined" onClick={() => setSelectedOrders([])}>Clear</Button>
                </Box>
            )}
            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
                <TableContainer component={Paper} className="tableShadow">
                    <Table sx={{ minWidth: { xs: 400, md: 700 } }} className="commonTable">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell padding="checkbox">
                                    <Checkbox
                                        sx={{ color: 'white' }}
                                        checked={getRes?.data?.length > 0 && selectedOrders.length === getRes.data.length}
                                        indeterminate={selectedOrders.length > 0 && selectedOrders.length < (getRes?.data?.length || 0)}
                                        onChange={handleSelectAll}
                                    />
                                </StyledTableCell>
                                <StyledTableCell>Accession</StyledTableCell>
                                <StyledTableCell>Patient</StyledTableCell>
                                <StyledTableCell>Tests</StyledTableCell>
                                <StyledTableCell>Priority</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Date</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getRes?.data?.length > 0 ? getRes.data.map(order => (
                                <StyledTableRow key={order._id}>
                                    <StyledTableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedOrders.includes(order._id)}
                                            onChange={() => handleSelectOrder(order._id)}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ fontWeight: 600 }}>{order.accessionNumber || order.labOrderId || '-'}</StyledTableCell>
                                    <StyledTableCell>
                                        {order.patientId ? `${order.patientId.fName || ''} ${order.patientId.lName || ''}`.trim() : '-'}
                                        {order.patientId?.mrn && <Typography variant="caption" display="block" color="text.secondary">{order.patientId.mrn}</Typography>}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {order.tests?.slice(0, 3).map((t, i) => (
                                                <Chip key={i} label={t.testId?.testCode || '?'} size="small" variant="outlined" />
                                            ))}
                                            {order.tests?.length > 3 && <Chip label={`+${order.tests.length - 3}`} size="small" />}
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={order.priority} size="small" color={priorityColors[order.priority] || 'default'} sx={{ textTransform: 'capitalize' }} />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={order.status} size="small" color={statusColors[order.status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                                    </StyledTableCell>
                                    <StyledTableCell>{order.orderedAt ? new Date(order.orderedAt).toLocaleDateString('en-GB') : '-'}</StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Tooltip title="View detail">
                                            <IconButton color="primary" size="small" onClick={() => navigate(`${basePath}/laboratory/orders/${order._id}`, { state: { from: location.pathname + location.search } })}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Print labels">
                                            <IconButton size="small" onClick={() => handlePrintLabels(order._id)}>
                                                <PrintIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {order.status !== 'cancelled' && order.status !== 'released' && (
                                            <Tooltip title="Cancel order">
                                                <IconButton color="error" size="small" onClick={() => handleCancel(order._id)}>
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8}><Typography>No lab orders found</Typography></StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {pageCount > 0 && <Pagination count={pageCount} page={currentPage} onChange={(e, v) => setCurrentPage(v)} />}

            <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Cancel Order</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to cancel this order?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelOpen(false)}>Back</Button>
                    <Button variant="contained" color="error" onClick={confirmCancel}>Cancel Order</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
